// ============================================================================
// BOQ — sticky horizontal scroll bar for wide tables
// ============================================================================
// A wide QS table scrolls sideways inside its own box, so its scroll bar sits
// at the bottom of that box. When the box runs below the window, the bar is off
// screen and the only way to reach the far columns is to scroll the page down
// first. This keeps a copy of the bar pinned to the bottom of the window while
// the table is on screen and its own bar is not, and drives the table from it.
//
// Usage: swap a scroll wrapper <div> for <StickyScrollBox> (same props), or give
// the wrapper a ref and render, anywhere beside it,
//   <StickyScrollbar targetRef={ref} />
// For a scroller nested inside the ref'd element, such as AG Grid's horizontal
// viewport, add selector=".ag-body-horizontal-scroll-viewport".
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';

const BAR_HEIGHT = 14;

export function StickyScrollbar({ targetRef, selector }) {
  const barRef = useRef(null);
  const getScroller = useCallback(() => {
    const root = targetRef.current;
    if (!root || !selector) return root;
    return root.querySelector(selector);
  }, [targetRef, selector]);
  // A grid builds its scroller after it mounts, so look again until it exists.
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (getScroller() || tries >= 20) return undefined;
    const t = setTimeout(() => setTries((n) => n + 1), 250);
    return () => clearTimeout(t);
  }, [getScroller, tries]);
  const [box, setBox] = useState(null); // { left, width, scrollWidth } while shown

  const measure = useCallback(() => {
    const el = getScroller();
    if (!el) { setBox(null); return; }
    const overflows = el.scrollWidth > el.clientWidth + 1;
    const r = el.getBoundingClientRect();
    // "On screen" is judged on the whole table box. For a plain wrapper that is
    // the scroller itself; for AG Grid the scroller is only the thin bar strip at
    // the grid's foot, whose top is off screen exactly when we need to show.
    const table = (targetRef.current || el).getBoundingClientRect();
    const vh = window.innerHeight;
    // Show only while the table is on screen but its own bar (at r.bottom) is not.
    const show = overflows && table.top < vh - BAR_HEIGHT * 3 && r.bottom > vh;
    setBox((prev) => {
      if (!show) return null;
      const next = { left: r.left, width: r.width, scrollWidth: el.scrollWidth };
      return prev && prev.left === next.left && prev.width === next.width && prev.scrollWidth === next.scrollWidth
        ? prev : next;
    });
  }, [getScroller, targetRef]);

  // Re-measure on page scroll, resize, and whenever the table changes size.
  useEffect(() => {
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    let ro;
    const el = getScroller();
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [getScroller, measure, tries]);

  // Keep the two bars in step, whichever one moved. Each side writes only when
  // the values differ, so the echo of a write finds them equal and stops there.
  useEffect(() => {
    const el = getScroller();
    const bar = barRef.current;
    if (!el || !bar || !box) return undefined;
    bar.scrollLeft = el.scrollLeft;
    const fromTable = () => {
      if (Math.abs(bar.scrollLeft - el.scrollLeft) > 0.5) bar.scrollLeft = el.scrollLeft;
    };
    const fromBar = () => {
      if (Math.abs(el.scrollLeft - bar.scrollLeft) > 0.5) el.scrollLeft = bar.scrollLeft;
    };
    el.addEventListener('scroll', fromTable);
    bar.addEventListener('scroll', fromBar);
    return () => {
      el.removeEventListener('scroll', fromTable);
      bar.removeEventListener('scroll', fromBar);
    };
  }, [getScroller, box]);

  if (!box) return null;
  return (
    <div
      ref={barRef}
      className="qs-sticky-scrollbar"
      aria-hidden="true"
      style={{
        position: 'fixed', bottom: 0, left: box.left, width: box.width, height: BAR_HEIGHT,
        overflowX: 'auto', overflowY: 'hidden', zIndex: 40,
        background: 'var(--brio-surface-muted, #f5f5f5)', borderTop: '1px solid var(--brio-border, #ddd)',
      }}
    >
      <div style={{ width: box.scrollWidth, height: 1 }} />
    </div>
  );
}

// Drop-in for a plain scroll wrapper <div>: same props, plus the sticky bar.
export function StickyScrollBox({ children, ...divProps }) {
  const ref = useRef(null);
  return (
    <>
      <div ref={ref} {...divProps}>{children}</div>
      <StickyScrollbar targetRef={ref} />
    </>
  );
}

export default StickyScrollbar;
